import { Fragment, useState, useEffect, useMemo } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  BuildingOffice2Icon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  TrophyIcon,
  SparklesIcon,
  ChevronDownIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { partnerFormSchema, type PartnerFormData, type PartnerData } from '../lib/validation';
import { registerPartner } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { getTournaments } from '../lib/supabase';
import { useSponsorshipTiers } from '../hooks/useSponsorshipTiers';

interface PartnerFormProps {
  isOpen: boolean;
  onClose: () => void;
  isAdmin?: boolean;
  defaultTournamentId?: string;
}

export default function PartnerForm({ isOpen, onClose, isAdmin = false, defaultTournamentId }: PartnerFormProps) {
  const { tiers: allTiers } = useSponsorshipTiers();

  const tierContributions = useMemo(() => {
    const map: Record<string, number> = {};
    allTiers.forEach(t => { map[t.name] = t.min_contribution; });
    return map;
  }, [allTiers]);

  const tierBenefits = useMemo(() => {
    const map: Record<string, string[]> = {};
    allTiers.forEach(t => { map[t.name] = t.features; });
    return map;
  }, [allTiers]);

  const getTierColor = (tier: string) => {
    const found = allTiers.find(t => t.name === tier);
    return found?.color_class || 'bg-green-100 text-green-800 border-green-200';
  };

  const standardTiers = useMemo(() => allTiers.filter(t => !t.is_category_tier && t.name !== 'Tournament Host'), [allTiers]);
  const categoryTiers = useMemo(() => allTiers.filter(t => t.is_category_tier), [allTiers]);

  const [mobileStep, setMobileStep] = useState(1);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [tournaments, setTournaments] = useState<{ id: string; name: string; status: string }[]>([]);
  const [selectedTournament, setSelectedTournament] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    trigger,
    getValues,
  } = useForm<PartnerFormData>({
    resolver: zodResolver(partnerFormSchema),
  });

  useEffect(() => {
    const fetchTournaments = async () => {
      try {
        const { data, error } = await getTournaments();
        if (error) throw error;
        if (data && data.length > 0) {
          const all = data.map(t => ({ id: t.id, name: t.name, status: t.status }));
          setTournaments(all);
          const initialId = defaultTournamentId ?? all.find(t => t.status === 'upcoming')?.id ?? all[0]?.id;
          if (initialId) {
            setSelectedTournament(initialId);
            setValue('tournamentId', initialId);
          }
        }
      } catch {
        setError('Failed to load tournaments');
      }
    };
    if (isOpen) {
      fetchTournaments();
      setMobileStep(1);
      setError('');
      setShowConfirmation(false);
    }
  }, [isOpen, setValue, defaultTournamentId]);

  const selectedTier = watch('sponsorshipTier');
  const selectedCategoryInterest = watch('categoryTierInterest');
  const isDiamondSelected = selectedTier === 'Diamond';

  const handleClose = () => {
    setShowConfirmation(false);
    setError('');
    setMobileStep(1);
    reset();
    onClose();
  };

  const handleMobileNext = async () => {
    const valid = await trigger(['tournamentId', 'companyName', 'contactPerson', 'email', 'phone']);
    if (!valid) { setError('Please fill in all required fields correctly'); return; }
    setError('');
    setMobileStep(2);
  };

  const onSubmit = async (data: PartnerFormData) => {
    try {
      setIsSubmitting(true);
      setError('');
      const partnerData: PartnerData = {
        company_name: data.companyName,
        contact_person: data.contactPerson,
        email: data.email,
        phone: data.phone,
        tier: data.sponsorshipTier,
        status: 'pending',
        contribution: tierContributions[data.sponsorshipTier],
        start_date: new Date().toISOString().split('T')[0] ?? '',
        end_date: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0] ?? '',
        benefits: tierBenefits[data.sponsorshipTier],
        tournament_id: data.tournamentId,
        description: data.categoryTierInterest
          ? `Category tier interest: ${data.categoryTierInterest}`
          : undefined,
      };
      const { error: registrationError } = await registerPartner(partnerData);
      if (registrationError) throw new Error(registrationError.message);
      setShowConfirmation(true);
      toast.success('Partner interest submitted successfully');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during registration');
      toast.error('Failed to submit partner interest');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const inputClass = 'w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50 transition-colors';
  const labelClass = 'block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide';
  const contribution = selectedTier ? tierContributions[selectedTier] : undefined;
  const benefits = (selectedTier ? tierBenefits[selectedTier] : undefined) || [];
  const tierColor = getTierColor(selectedTier);
  const companyName = getValues('companyName');

  const mobileSheet = isOpen && (
    <div className="fixed inset-0 z-50 flex flex-col justify-end sm:hidden">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={!showConfirmation ? handleClose : undefined} />
      <div className="relative w-full bg-white rounded-t-3xl shadow-2xl max-h-[94vh] flex flex-col">

                <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100 flex-shrink-0">
                  <div>
                    <h2 className="text-base font-bold text-golf-gray">
                      {showConfirmation ? 'Interest Submitted' : 'Partner Interest'}
                    </h2>
                    {!showConfirmation && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        Step {mobileStep} of 2 — {mobileStep === 1 ? 'Contact Details' : 'Sponsorship Tier'}
                      </p>
                    )}
                  </div>
                  {!showConfirmation && (
                    <button
                      onClick={handleClose}
                      className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 active:bg-gray-200 transition-colors"
                    >
                      <XMarkIcon className="w-4 h-4 text-gray-500" />
                    </button>
                  )}
                </div>

                {!showConfirmation && (
                  <div className="flex gap-1.5 px-5 pt-3 pb-1 flex-shrink-0">
                    {[1, 2].map(s => (
                      <div
                        key={s}
                        className={`h-1 flex-1 rounded-full transition-all duration-300 ${s <= mobileStep ? 'bg-golf-orange' : 'bg-gray-200'}`}
                      />
                    ))}
                  </div>
                )}

                <div className="flex-1 overflow-y-auto px-5 py-4">
                  {showConfirmation ? (
                    <div className="flex flex-col items-center text-center py-8 space-y-4">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                        <CheckCircleIcon className="w-10 h-10 text-green-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-golf-gray">Thank You for Your Interest!</h3>
                        <p className="text-sm text-gray-500 mt-1">
                          A member of our planning committee will reach out to discuss partnership opportunities.
                        </p>
                      </div>
                      <div className="bg-golf-sand/40 rounded-2xl px-6 py-4 w-full space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Company</span>
                          <span className="font-semibold text-golf-gray">{companyName}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Tier</span>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${getTierColor(selectedTier)}`}>
                            {selectedTier}
                          </span>
                        </div>
                        {selectedCategoryInterest && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Category Interest</span>
                            <span className="text-xs font-semibold text-sky-700 bg-sky-50 border border-sky-200 px-2 py-0.5 rounded-full">
                              {selectedCategoryInterest}
                            </span>
                          </div>
                        )}
                      </div>
                      <button
                        onClick={handleClose}
                        className="w-full py-3 rounded-xl bg-golf-orange text-white font-semibold text-sm mt-2"
                      >
                        Done
                      </button>
                    </div>
                  ) : mobileStep === 1 ? (
                    <div className="space-y-5">
                      {error && (
                        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-600">{error}</div>
                      )}

                      {tournaments.length > 0 && (
                        <div>
                          <label className={labelClass}>Tournament *</label>
                          <div className="relative">
                            <TrophyIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                            <select
                              {...register('tournamentId')}
                              className={`${inputClass} pl-10 appearance-none`}
                              onChange={e => { setSelectedTournament(e.target.value); setValue('tournamentId', e.target.value); }}
                              value={selectedTournament}
                            >
                              <option value="">Select a tournament</option>
                              {['upcoming', 'ongoing', 'completed'].map(status => {
                                const group = tournaments.filter(t => t.status === status);
                                if (!group.length) return null;
                                return (
                                  <optgroup key={status} label={status.charAt(0).toUpperCase() + status.slice(1)}>
                                    {group.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                  </optgroup>
                                );
                              })}
                            </select>
                            <ChevronDownIcon className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                          </div>
                          {errors.tournamentId && <p className="mt-1 text-xs text-red-500">{errors.tournamentId.message}</p>}
                        </div>
                      )}

                      <div className="bg-gray-50 rounded-2xl p-4 space-y-4">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide flex items-center gap-1.5">
                          <BuildingOffice2Icon className="w-3.5 h-3.5" /> Company
                        </p>
                        <div>
                          <label className={labelClass}>Company Name *</label>
                          <div className="relative">
                            <BuildingOffice2Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                            <input type="text" {...register('companyName')} placeholder="e.g. Acme Corporation" className={`${inputClass} pl-10`} />
                          </div>
                          {errors.companyName && <p className="mt-1 text-xs text-red-500">{errors.companyName.message}</p>}
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-2xl p-4 space-y-4">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide flex items-center gap-1.5">
                          <UserIcon className="w-3.5 h-3.5" /> Contact
                        </p>
                        <div>
                          <label className={labelClass}>Contact Person *</label>
                          <div className="relative">
                            <UserIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                            <input type="text" {...register('contactPerson')} placeholder="Full name" className={`${inputClass} pl-10`} />
                          </div>
                          {errors.contactPerson && <p className="mt-1 text-xs text-red-500">{errors.contactPerson.message}</p>}
                        </div>
                        <div>
                          <label className={labelClass}>Email *</label>
                          <div className="relative">
                            <EnvelopeIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                            <input type="email" {...register('email')} placeholder="contact@company.com" className={`${inputClass} pl-10`} />
                          </div>
                          {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
                        </div>
                        <div>
                          <label className={labelClass}>Phone *</label>
                          <div className="relative">
                            <PhoneIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                            <input type="tel" {...register('phone')} placeholder="+234..." className={`${inputClass} pl-10`} />
                          </div>
                          {errors.phone && <p className="mt-1 text-xs text-red-500">{errors.phone.message}</p>}
                        </div>
                      </div>

                      <div className="pb-2" />
                    </div>
                  ) : (
                    <div className="space-y-5">
                      {error && (
                        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-600">{error}</div>
                      )}

                      <div>
                        <label className={labelClass}>Preferred Sponsorship Tier *</label>
                        <div className="relative">
                          <SparklesIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                          <select {...register('sponsorshipTier')} className={`${inputClass} pl-10 appearance-none`}>
                            <option value="">Select a tier</option>
                            {standardTiers.map(t => (
                              <option key={t.id} value={t.name}>{t.name}{t.price_label ? ` (${t.price_label})` : ''}</option>
                            ))}
                            {isAdmin && categoryTiers.map(t => (
                              <option key={t.id} value={t.name}>{t.name}</option>
                            ))}
                          </select>
                          <ChevronDownIcon className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        </div>
                        {errors.sponsorshipTier && <p className="mt-1 text-xs text-red-500">{errors.sponsorshipTier.message}</p>}
                      </div>

                      {isDiamondSelected && (
                        <div className="bg-sky-50 border border-sky-200 rounded-2xl p-4 space-y-3">
                          <div>
                            <p className="text-xs font-semibold text-sky-700 uppercase tracking-wide">Diamond — Category Exclusivity</p>
                            <p className="text-xs text-sky-600 mt-0.5">As a Diamond sponsor, you may select a category tier of interest. Our team will confirm availability.</p>
                          </div>
                          <div>
                            <label className={labelClass}>Category Tier Interest</label>
                            <div className="relative">
                              <SparklesIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-sky-400 pointer-events-none" />
                              <select {...register('categoryTierInterest')} className={`${inputClass} pl-10 appearance-none border-sky-200 bg-white focus:border-sky-400`}>
                                <option value="">Select a category (optional)</option>
                                {categoryTiers.map(t => (
                                  <option key={t.id} value={t.name}>{t.name}{t.price_label ? ` (${t.price_label})` : ''}</option>
                                ))}
                              </select>
                              <ChevronDownIcon className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-sky-400 pointer-events-none" />
                            </div>
                          </div>
                        </div>
                      )}

                      {selectedTier && (
                        <div className="rounded-2xl border border-gray-100 overflow-hidden">
                          <div className="bg-golf-sand/40 px-4 py-3 flex items-center justify-between border-b border-gray-100">
                            <div>
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Selected Tier</p>
                              <span className={`mt-1 inline-block text-xs font-bold px-2.5 py-1 rounded-full border ${tierColor}`}>
                                {selectedTier}
                              </span>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-gray-400 mb-0.5">Contribution</p>
                              <p className="text-base font-bold text-golf-gray">
                                {contribution == null || contribution === 0 ? 'Discuss' : `₦${(contribution / 1_000_000).toFixed(0)}M`}
                              </p>
                            </div>
                          </div>
                          <div className="px-4 py-3">
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2.5">Benefits Included</p>
                            <div className="flex flex-wrap gap-1.5">
                              {benefits.map(b => (
                                <span key={b} className="text-xs bg-white border border-gray-200 text-golf-gray rounded-lg px-2.5 py-1 font-medium">
                                  {b}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="bg-golf-sand/30 rounded-xl px-4 py-3 border border-golf-sand/60">
                        <p className="text-xs text-gray-500 leading-relaxed">
                          Submitting this form registers your company's interest. Our sponsorship team will reach out within 2 business days to finalize the agreement.
                        </p>
                      </div>

                      <div className="pb-2" />
                    </div>
                  )}
                </div>

                {!showConfirmation && (
                  <div className="flex gap-3 px-5 pt-4 pb-20 border-t border-gray-100 flex-shrink-0">
                    {mobileStep === 2 ? (
                      <button
                        type="button"
                        onClick={() => { setMobileStep(1); setError(''); }}
                        className="flex-1 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 bg-white active:bg-gray-50 transition-colors"
                      >
                        Back
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handleClose}
                        className="flex-1 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 bg-white active:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                    )}
                    {mobileStep === 1 ? (
                      <button
                        type="button"
                        onClick={handleMobileNext}
                        className="flex-1 py-3 rounded-xl bg-golf-orange text-white text-sm font-semibold active:bg-golf-orange/90 transition-colors"
                      >
                        Next: Choose Tier
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handleSubmit(onSubmit)}
                        disabled={isSubmitting}
                        className="flex-1 py-3 rounded-xl bg-golf-orange text-white text-sm font-semibold active:bg-golf-orange/90 disabled:opacity-60 transition-colors"
                      >
                        {isSubmitting ? 'Submitting...' : 'Submit Interest'}
                      </button>
                    )}
                  </div>
                )}
      </div>
    </div>
  );

  const desktopModal = (
    <Transition.Root show={isOpen && !isMobile} as={Fragment}>
      <Dialog as="div" className="relative z-50 hidden sm:block" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 scale-95"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 scale-100"
              leaveTo="opacity-0 translate-y-4 scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-xl transition-all w-full max-w-lg">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-golf-orange focus:ring-offset-2"
                    onClick={handleClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                {showConfirmation ? (
                  <div className="bg-white px-6 pb-6 pt-5">
                    <div className="text-center py-8">
                      <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-green-100 mb-6">
                        <CheckCircleIcon className="h-12 w-12 text-green-600" />
                      </div>
                      <h3 className="text-2xl font-display font-bold text-golf-gray mb-4">
                        Thank You for Your Interest!
                      </h3>
                      <p className="text-gray-600 mb-8">
                        A member of our planning committee will reach out to you shortly to discuss partnership opportunities.
                      </p>
                      <button
                        type="button"
                        onClick={handleClose}
                        className="modern-button px-8 py-3 text-white text-lg font-medium"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit(onSubmit)} className="bg-white">
                    <div className="border-b border-gray-200 bg-golf-sand/30 px-6 py-5">
                      <Dialog.Title as="h3" className="text-2xl font-display font-bold text-golf-gray">
                        Partner Interest Form
                      </Dialog.Title>
                      <p className="mt-1 text-sm text-gray-600">
                        Please fill out the form below to express your interest in partnering with us.
                      </p>
                    </div>

                    <div className="px-6 py-5 space-y-6">
                      {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-600 text-sm">{error}</div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Tournament <span className="text-red-500">*</span></label>
                        <select
                          {...register('tournamentId')}
                          className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                          onChange={e => { setSelectedTournament(e.target.value); setValue('tournamentId', e.target.value); }}
                          value={selectedTournament}
                        >
                          <option value="">Select a tournament</option>
                          {['upcoming', 'ongoing', 'completed'].map(status => {
                            const group = tournaments.filter(t => t.status === status);
                            if (!group.length) return null;
                            return (
                              <optgroup key={status} label={status.charAt(0).toUpperCase() + status.slice(1)}>
                                {group.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                              </optgroup>
                            );
                          })}
                        </select>
                        {errors.tournamentId && <p className="mt-1 text-sm text-red-600">{errors.tournamentId.message}</p>}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Company Name <span className="text-red-500">*</span></label>
                        <input type="text" {...register('companyName')} className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange" />
                        {errors.companyName && <p className="mt-1 text-sm text-red-600">{errors.companyName.message}</p>}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Contact Person <span className="text-red-500">*</span></label>
                        <input type="text" {...register('contactPerson')} className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange" />
                        {errors.contactPerson && <p className="mt-1 text-sm text-red-600">{errors.contactPerson.message}</p>}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Email Address <span className="text-red-500">*</span></label>
                        <input type="email" {...register('email')} className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange" />
                        {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Phone Number <span className="text-red-500">*</span></label>
                        <input type="tel" {...register('phone')} className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange" />
                        {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Preferred Sponsorship Tier <span className="text-red-500">*</span></label>
                        <select {...register('sponsorshipTier')} className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange">
                          <option value="">Select a tier</option>
                          {standardTiers.map(t => (
                            <option key={t.id} value={t.name}>{t.name}{t.price_label ? ` (${t.price_label})` : ''}</option>
                          ))}
                          {isAdmin && categoryTiers.length > 0 && (
                            <optgroup label="Category Exclusivity">
                              {categoryTiers.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                            </optgroup>
                          )}
                        </select>
                        {errors.sponsorshipTier && <p className="mt-1 text-sm text-red-600">{errors.sponsorshipTier.message}</p>}
                      </div>

                      {isDiamondSelected && (
                        <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 space-y-3">
                          <div>
                            <p className="text-sm font-semibold text-sky-800">Diamond — Category Exclusivity</p>
                            <p className="text-xs text-sky-600 mt-0.5">As a Diamond sponsor, you may select a category tier of interest. Our team will confirm availability.</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-sky-700">Category Tier Interest</label>
                            <select {...register('categoryTierInterest')} className="mt-1 block w-full rounded-lg border-sky-300 bg-white shadow-sm focus:border-sky-500 focus:ring-sky-500 text-sm">
                              <option value="">Select a category (optional)</option>
                              {categoryTiers.map(t => (
                                <option key={t.id} value={t.name}>{t.name}{t.price_label ? ` (${t.price_label})` : ''}</option>
                              ))}
                            </select>
                          </div>
                          {selectedCategoryInterest && (
                            <div className="bg-white border border-sky-200 rounded-lg p-3">
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Category Benefits</p>
                              <ul className="space-y-1">
                                {(tierBenefits[selectedCategoryInterest] ?? []).map((b, i) => (
                                  <li key={i} className="flex items-center text-xs text-gray-600">
                                    <svg className="w-3.5 h-3.5 text-sky-500 mr-1.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    {b}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}

                      {selectedTier && (
                        <div className="bg-golf-sand/30 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-golf-gray mb-2">{selectedTier} Tier Benefits</h4>
                          <ul className="space-y-2">
                            {tierBenefits[selectedTier]?.map((benefit, i) => (
                              <li key={i} className="flex items-center text-sm text-gray-600">
                                <svg className="w-4 h-4 text-golf-orange mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                {benefit}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    <div className="bg-gray-50 px-6 py-4 flex flex-row-reverse gap-3">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="modern-button px-8 py-3 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? 'Submitting...' : 'Submit'}
                      </button>
                      <button
                        type="button"
                        className="px-8 py-3 bg-white text-gray-700 border border-gray-300 rounded-full hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-golf-orange"
                        onClick={handleClose}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );

  return (
    <>
      {mobileSheet}
      {desktopModal}
    </>
  );
}
